import { Container, Stage } from '@pixi/react'
import { initDevtools } from '@pixi/devtools'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Application } from 'pixi.js'
import { Size } from '../model/Size'
import { calculateActualSize } from '../util/pixiUtils'
import { PixiAppContext } from '../store/PixiAppContext'
import { setApp, getAppResolution } from '../../util/index'
import { PixiPlugin } from 'gsap/PixiPlugin'
import * as PIXI from 'pixi.js'
import { gsap } from 'gsap'
import { isMobile } from '../../util/deviceUtil'
import OPBetDetailScaleButtons from './OPBetDetailScaleButton'
import { Image } from '../../UI/Image'

// 註冊插件
gsap.registerPlugin(PixiPlugin)
PixiPlugin.registerPIXI(PIXI)

interface PixiAppProps {
  element: HTMLElement
  resolution: Size
  children: React.ReactNode
  stageBackgroundImage?: string
}

/**
 * 根據屏幕和目標分辨率計算實際渲染寬高
 */

export const useCalculatedSize = (element: HTMLElement, resolution: Size) => {
  const screenSize = { width: element.clientWidth, height: element.clientHeight }
  const [actualSize, setActualSize] = useState(calculateActualSize(screenSize, resolution))

  const recalculateActualSize = useCallback(() => {
    const currentScreenSize = {
      width: element.clientWidth,
      height: element.clientHeight,
    }
    setActualSize(calculateActualSize(currentScreenSize, resolution))
  }, [element, resolution])

  useEffect(() => {
    window.addEventListener('resize', recalculateActualSize)
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible')
        recalculateActualSize()
    }
    window.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('orientationchange', recalculateActualSize)

    return () => {
      window.removeEventListener('resize', recalculateActualSize)
      window.removeEventListener('orientationchange', recalculateActualSize)
      window.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [recalculateActualSize])

  return actualSize
}

export const PixiApp = ({ children, resolution, element, stageBackgroundImage }: PixiAppProps) => {
  const actualSize = useCalculatedSize(element, resolution)
  // 應用初始化配置
  const onAppMount = useCallback((app: Application) => {
    const resizeCB = () => {
      app.resize()
    }

    // 初始化開發工具
    initDevtools({ app })
    window.__PIXI_APP__ = app
    setApp(app)

    return () => {
      window.onresize = null
      window.removeEventListener('orientationchange', resizeCB)
      document.onvisibilitychange = null
    }
  }, [])

  return (
    <Stage
      width={element.clientWidth}
      height={element.clientHeight}
      options={{
        antialias: false,
        autoDensity: true,
        resolution: getAppResolution(),
        resizeTo: element,
        background: '#000',
      }}
      onMount={onAppMount}
    >
      <PixiAppContext.Provider value={{ resolution }}>
        {stageBackgroundImage && (
          <Image
            name="StageBackground"
            image={stageBackgroundImage}
            width={element.clientWidth}
            height={element.clientHeight}
            x={element.clientWidth / 2}
            y={element.clientHeight / 2}
            anchor={[0.5, 0.5]}
          />
        )}
        <Container
          name="App"
          position={{
            x: (element.clientWidth - actualSize.width) / 2,
            y: (element.clientHeight - actualSize.height) / 2,
          }}
          scale={actualSize.width / resolution.width}
        >
          {children}
        </Container>
      </PixiAppContext.Provider>
    </Stage>
  )
}

export function PixiBetDetailApp(props: PixiAppProps) {
  const { children, resolution, element } = props
  const [forceScale, setForceScale] = useState(1)
  const actualSize = useCalculatedSize(element, resolution)
  const minResolution = isMobile() ? actualSize : { width: 450, height: 800 }
  const displaySize = {
    width: Math.max(actualSize.width, minResolution.width),
    height: Math.max(actualSize.height, minResolution.height),
  }
  const isMouseInsideContainerRef = useRef(false)
  const [globalScrollTop, setGlobalScrollTop] = useState(0)
  // 應用初始化配置
  const onAppMount = useCallback((app: Application) => {
    const resizeCB = () => {
      app.resize()
    }

    // 初始化開發工具
    initDevtools({ app })
    window.__PIXI_APP__ = app
    setApp(app)

    return () => {
      window.onresize = null
      window.removeEventListener('orientationchange', resizeCB)
      document.onvisibilitychange = null
    }
  }, [])

  useEffect(() => {
    const updateGlobalScrollTop = () => setGlobalScrollTop(element.scrollTop)
    element.addEventListener('scroll', updateGlobalScrollTop)
    return () => element.removeEventListener('scroll', updateGlobalScrollTop)
  }, [element])

  return (
    <Stage
      width={element.offsetWidth}
      height={displaySize.height * forceScale}
      options={{
        antialias: false,
        autoDensity: true,
        resolution: getAppResolution(),
        background: '#000',
      }}
      onMount={onAppMount}
    >
      <PixiAppContext.Provider value={{ resolution }}>
        <Container
          name="App"
          position={{
            x: (element.offsetWidth - displaySize.width * forceScale) / 2,
            y: 0,
          }}
          scale={displaySize.width / resolution.width * forceScale}
          onmouseenter={() => isMouseInsideContainerRef.current = true}
          onmouseleave={() => isMouseInsideContainerRef.current = false}
        >
          {children}
        </Container>
        {!isMobile() && (
          <OPBetDetailScaleButtons
            x={element.offsetWidth - 80}
            y={element.offsetHeight - 36 + globalScrollTop}
            size={32}
            onMinus={() => setForceScale(prev => prev - 0.1)}
            onPlus={() => setForceScale(prev => prev + 0.1)}
          />
        )}
      </PixiAppContext.Provider>
    </Stage>
  )
}
